import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StaffMember, StaffPaginatedResponse } from '../../models/StaffMember';
import { StaffService } from '../../services/staff.service';
import { DepartmentService } from '../../services/department.service';
import { TableAction } from '../../shared/components/table/table.component';
import { ConfirmationService } from '../../shared/services/confirmation.service';
import { ErrorService } from '../../shared/services/error.service';

@Component({
  selector: 'app-staff',
  templateUrl: './staff.component.html',
  styleUrls: ['./staff.component.css']
})
export class StaffComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() enableSalaryRaise: boolean = false;

  staff: StaffMember[] = [];
  loading = false;
  error: string | null = null;
  searchTerm = '';
  sortBy: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;
  
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  /** Filtros da lista (ao lado do botão Exportar Excel) */
  listFilters = {
    department: '',
    status: '' as string
  };
  departmentOptions: { value: string; label: string }[] = [];
  statusOptions: { value: string; label: string }[] = [
    { value: '', label: 'Todos os Status' },
    { value: 'active', label: 'Ativo' },
    { value: 'inactive', label: 'Desativado' },
    { value: 'blocked', label: 'Bloqueado' }
  ];
  loadingDepartments = false;

  columns: { titulo: string, label: string, formatter?: (value: any, row?: any) => string }[] = [
    { titulo: 'ID', label: 'Id' },
    { titulo: 'Nome', label: 'FullName' },
    { titulo: 'Matrícula', label: 'Registration' },
    { titulo: 'Cargo', label: 'Position' },
    { titulo: 'Departamento', label: 'Department' },
    { titulo: 'Gerente', label: 'Manager', formatter: (value: any) => this.formatManager(value) },
    { titulo: 'Status', label: 'StatusDisplay' },
    { titulo: 'Data Admissão', label: 'HireDate', formatter: (value: any) => this.formatDate(value) }
  ];

  actions: TableAction[] = [];

  // Modal de Relatórios
  showReportModal = false;
  isGeneratingReport = false;
  
  // Campos disponíveis para exportação
  availableFields = [
    { key: 'id', label: 'ID', checked: true },
    { key: 'fullName', label: 'Nome Completo', checked: true },
    { key: 'cpf', label: 'CPF', checked: false },
    { key: 'registration', label: 'Matrícula', checked: true },
    { key: 'contractType', label: 'Tipo de Contrato', checked: false },
    // Campos específicos de PJ (Pessoa Jurídica)
    { key: 'cnpj', label: 'CNPJ', checked: false },
    { key: 'companyName', label: 'Razão Social', checked: false },
    { key: 'tradeName', label: 'Nome Fantasia', checked: false },
    { key: 'companyAddress', label: 'Endereço da Empresa', checked: false },
    { key: 'companyEmail', label: 'E-mail da Empresa', checked: false },
    { key: 'companyPhone', label: 'Telefone da Empresa', checked: false },
    // Campos gerais
    { key: 'department', label: 'Departamento', checked: true },
    { key: 'position', label: 'Cargo', checked: true },
    { key: 'hireDate', label: 'Data de Admissão', checked: true },
    { key: 'email', label: 'E-mail', checked: false },
    { key: 'phone', label: 'Telefone', checked: false }
  ];

  // Filtros
  reportFilters = {
    department: '',
    isActive: null as boolean | null,
    contractType: '' as string | null
  };

  // Opções adicionais
  reportOptions = {
    includeContacts: false,
    includeAddresses: false,
    includeSalary: false,
    includeBenefits: false
  };

  // Aumento salarial individual
  showSalaryRaiseModal = false;
  selectedStaffForRaise: StaffMember | null = null;
  isSavingSalaryRaise = false;
  isLoadingCurrentSalary = false;
  currentSalary: {
    baseSalary: number;
    grossSalary: number;
    netSalary: number;
    currency: string;
    salaryType: string;
    effectiveDate: string;
    isActive: boolean;
    reason?: string | null;
    notes?: string | null;
  } | null = null;
  salaryRaisePayload = {
    baseSalary: null as number | null,
    grossSalary: null as number | null,
    netSalary: null as number | null,
    currency: 'BRL',
    salaryType: 'Mensal',
    effectiveDate: '',
    isActive: true,
    reason: '',
    notes: ''
  };

  /** Demissão — modal com data de desligamento escolhida pelo usuário */
  showTerminateStaffModal = false;
  terminateStaffTarget: StaffMember | null = null;
  terminationDateInput = '';

  // Dissídio coletivo (CLT)
  showDissidioModal = false;
  isApplyingDissidio = false;
  dissidioPayload = {
    percentage: 0,
    effectiveDate: ''
  };
  dissidioResult: {
    percentage: number;
    effectiveDate: string;
    reason: string | null;
    totalEmployees: number;
    updatedEmployees: number;
    skippedEmployees: number;
    totalPreviousSalary: number;
    totalNewSalary: number;
    totalIncrease: number;
    details: {
      staffId: number;
      staffName: string;
      registration: string;
      contractType: string;
      previousSalary: number;
      newSalary: number;
      increase: number;
      updated: boolean;
      reason: string | null;
    }[];
  } | null = null;

  constructor(
    private router: Router,
    private staffService: StaffService,
    private departmentService: DepartmentService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadDepartments();
    this.loadStaff();
  }

  loadDepartments(): void {
    this.loadingDepartments = true;
    this.departmentService.getAllDepartments().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (departments) => {
        this.departmentOptions = [
          { value: '', label: 'Todos os Departamentos' },
          ...(departments || []).map((d: { id: number; name?: string }) => ({
            value: String(d.id),
            label: d.name || ''
          }))
        ];
        this.loadingDepartments = false;
      },
      error: () => {
        this.loadingDepartments = false;
        this.departmentOptions = [{ value: '', label: 'Todos os Departamentos' }];
      }
    });
  }

  applyListFilters(): void {
    this.currentPage = 1;
    this.loadStaff();
  }

  clearListFilters(): void {
    this.listFilters = { department: '', status: '' };
    this.currentPage = 1;
    this.loadStaff();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStaff(): void {
    this.loading = true;
    this.error = null;

    const params: any = {
      page: this.currentPage,
      pageSize: this.pageSize
    };

    if (this.searchTerm?.trim()) {
      params.search = this.searchTerm.trim();
    }

    if (this.sortBy) {
      params.sortBy = this.sortBy;
      params.sortDirection = this.sortDirection || 'asc';
    }

    const department = this.listFilters.department?.trim() || '';
    if (department) {
      params.department = department;
    }

    const request$ = department
      ? this.staffService.getStaffByDepartment(department, params)
      : this.listFilters.status === 'active'
        ? this.staffService.getStaffByStatus('active', params)
        : this.listFilters.status === 'inactive'
          ? this.staffService.getStaffByStatus('inactive', params)
          : this.listFilters.status === 'blocked'
            ? this.staffService.getStaffByStatus('blocked', params)
            : this.staffService.getStaffPaginated(params);

    request$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: StaffPaginatedResponse) => {
        if (!response) {
          this.error = 'Resposta inválida da API';
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        const staffList = response.staff;
        if (!staffList || !Array.isArray(staffList)) {
          this.error = 'Formato de resposta inválido da API';
          this.staff = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        this.staff = this.prepareDataForDisplay(staffList);
        this.totalItems = response.totalCount ?? 0;
        this.totalPages = response.totalPages ?? 0;
        this.currentPage = response.page ?? this.currentPage;
        this.pageSize = response.pageSize ?? this.pageSize;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = error.message || 'Erro ao carregar funcionários';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadStaff();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadStaff();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadStaff();
  }

  /**
   * Recebe evento genérico de ordenação da tabela e mapeia para os campos do backend.
   */
  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    // Mapa de colunas do front -> campos do backend
    const sortMap: Record<string, string> = {
      FullName: 'fullname',
      HireDate: 'admissiondate',
      Id: 'id'
    };

    const backendField = sortMap[event.sortBy];

    if (!backendField) {
      return;
    }

    this.sortBy = backendField;
    this.sortDirection = event.sortOrder;
    this.currentPage = 1;
    this.loadStaff();
  }

  private prepareDataForDisplay(data: StaffMember[]): StaffMember[] {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    return data.map(item => ({
      ...item,
      Manager: item.Manager || null,
      CostCenter: item.CostCenter || null,
      Shift: item.Shift || null,
      WorkSchedule: item.WorkSchedule || null,
      Unit: item.Unit || null,
      Active: !item.Inactive,
      StatusDisplay: this.getStatusDisplay(item),
      _actions: this.getActionsForStaff(item)
    }));
  }

  private getActionsForStaff(staff: StaffMember): TableAction[] {
    const actions: TableAction[] = [];

    if (staff.Blocked) {
      actions.push({ label: 'Desbloquear', icon: 'fa-solid fa-unlock', action: 'unblock', class: 'btn-success', visible: false });
    } else {
      actions.push({ label: 'Bloquear', icon: 'fa-solid fa-ban', action: 'block', class: 'btn-warning', visible: false });
    }

    if (staff.Inactive) {
      actions.push({ label: 'Reativar', icon: 'fa-solid fa-user-check', action: 'reactivate', class: 'btn-success', visible: false });
    } else {
      actions.push({ label: 'Demitir', icon: 'fa-solid fa-user-slash', action: 'terminate', class: 'btn-warning', visible: false });
    }

    actions.push({ label: 'Excluir', icon: 'fa-solid fa-trash', action: 'delete', class: 'btn-danger', visible: false });

    if (this.enableSalaryRaise) {
      actions.push({
        label: 'Aumento Salarial',
        icon: 'fa-solid fa-money-bill-trend-up',
        action: 'salaryRaise',
        class: 'btn-info',
        visible: true
      });
    }

    return actions;
  }

  private getStatusDisplay(staff: StaffMember): string {
    if (staff.Blocked) return 'Bloqueado';
    if (staff.Inactive) return 'Desativado';
    return 'Ativo';
  }

  viewStaff(staff: StaffMember): void {
    if (!staff?.Id) {
      this.error = 'Erro: ID de funcionário inválido';
      return;
    }
    
    this.loading = true;
    this.error = null;

    this.staffService.getStaffComplete(staff.Id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (staffComplete) => {
        this.loading = false;
        
        if (!staffComplete?.Staff) {
          this.error = 'Dados incompletos recebidos da API';
          return;
        }
        
        this.router.navigate(['/hr/staff', staff.Id], { 
          state: { staffComplete, name: staff.FullName } 
        });
      },
      error: () => {
        this.loading = false;
        this.error = 'Erro ao carregar dados completos do funcionário';
        this.router.navigate(['/hr/staff', staff.Id], { 
          state: { name: staff.FullName, error: 'Erro ao carregar dados completos' } 
        });
      }
    });
  }

  goToNewStaffForm(): void {
    this.router.navigate(['/hr/staff/new']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  formatManager(manager: string | null): string {
    return manager || '-';
  }

  onActionClick(event: { action: string, item: StaffMember }): void {
    const { action, item } = event;

    switch (action) {
      case 'delete':
        this.confirmDeleteStaff(item);
        break;
      case 'terminate':
        this.openTerminateStaffModal(item);
        break;
      case 'block':
        this.confirmBlockStaff(item);
        break;
      case 'unblock':
        this.confirmUnblockStaff(item);
        break;
      case 'reactivate':
        this.confirmReactivateStaff(item);
        break;
      case 'salaryRaise':
        this.openSalaryRaiseModal(item);
        break;
    }
  }

  confirmDeleteStaff(staff: StaffMember): void {
    this.confirmationService.confirm({
      title: 'Confirmar Exclusão',
      message: `Tem certeza que deseja excluir o funcionário "${staff.FullName}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-danger',
      type: 'danger'
    }).pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (confirmed) {
        this.loading = true;
        this.staffService.deleteStaff(staff.Id).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loading = false;
            this.loadStaff();
          },
          error: () => {
            this.loading = false;
            this.error = 'Erro ao excluir funcionário';
          }
        });
      }
    });
  }

  openTerminateStaffModal(staff: StaffMember): void {
    this.error = null;
    this.terminateStaffTarget = staff;
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    this.terminationDateInput = `${y}-${m}-${day}`;
    this.showTerminateStaffModal = true;
  }

  closeTerminateStaffModal(): void {
    this.showTerminateStaffModal = false;
    this.terminateStaffTarget = null;
    this.terminationDateInput = '';
  }

  confirmTerminateStaff(): void {
    const staff = this.terminateStaffTarget;
    const raw = this.terminationDateInput?.trim();
    if (!staff?.Id) {
      this.error = 'Funcionário inválido.';
      return;
    }
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      this.error = 'Informe uma data de desligamento válida.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.staffService.terminateStaff(staff.Id, raw).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loading = false;
        this.closeTerminateStaffModal();
        this.loadStaff();
      },
      error: () => {
        this.loading = false;
        this.error = 'Erro ao registrar demissão';
      }
    });
  }

  confirmBlockStaff(staff: StaffMember): void {
    const blockDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    const blockDateFormatted = blockDate.toLocaleDateString('pt-BR');
    
    this.confirmationService.confirm({
      title: 'Confirmar Bloqueio',
      message: `Tem certeza que deseja bloquear o funcionário "${staff.FullName}"? O bloqueio será até ${blockDateFormatted} (30 dias).`,
      confirmText: 'Bloquear',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-warning',
      type: 'warning'
    }).pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (confirmed) {
        const blockPeriod = blockDate.toISOString().split('T')[0];
        this.loading = true;
        this.staffService.blockStaff(staff.Id, blockPeriod).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loading = false;
            this.loadStaff();
          },
          error: () => {
            this.loading = false;
            this.error = 'Erro ao bloquear funcionário';
          }
        });
      }
    });
  }

  confirmUnblockStaff(staff: StaffMember): void {
    this.confirmationService.confirm({
      title: 'Confirmar Desbloqueio',
      message: `Tem certeza que deseja desbloquear o funcionário "${staff.FullName}"?`,
      confirmText: 'Desbloquear',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-success',
      type: 'info'
    }).pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (confirmed) {
        this.loading = true;
        this.staffService.unblockStaff(staff.Id).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loading = false;
            this.loadStaff();
          },
          error: () => {
            this.loading = false;
            this.error = 'Erro ao desbloquear funcionário';
          }
        });
      }
    });
  }

  confirmReactivateStaff(staff: StaffMember): void {
    this.confirmationService.confirm({
      title: 'Confirmar Reativação',
      message: `Tem certeza que deseja reativar o funcionário "${staff.FullName}"?`,
      confirmText: 'Reativar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-success',
      type: 'info'
    }).pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      if (confirmed) {
        this.loading = true;
        this.staffService.reactivateStaff(staff.Id).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: () => {
            this.loading = false;
            this.loadStaff();
          },
          error: () => {
            this.loading = false;
            this.error = 'Erro ao reativar funcionário';
          }
        });
      }
    });
  }

  // ===== AUMENTO SALARIAL INDIVIDUAL =====

  openSalaryRaiseModal(staff: StaffMember): void {
    this.selectedStaffForRaise = staff;
    const today = new Date().toISOString().split('T')[0];
    // Valores padrão enquanto carrega o salário atual
    this.currentSalary = null;
    this.salaryRaisePayload = {
      baseSalary: null,
      grossSalary: null,
      netSalary: null,
      currency: 'BRL',
      salaryType: 'Mensal',
      effectiveDate: today,
      isActive: true,
      reason: '',
      notes: ''
    };
    this.showSalaryRaiseModal = true;

    if (!staff?.Id) {
      return;
    }

    this.isLoadingCurrentSalary = true;
    this.staffService.getCurrentStaffSalary(staff.Id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (salary) => {
        this.isLoadingCurrentSalary = false;
        if (!salary) {
          return;
        }
        const apiDate = salary.effectiveDate ? salary.effectiveDate.substring(0, 10) : today;
        this.currentSalary = {
          baseSalary: salary.baseSalary,
          grossSalary: salary.grossSalary,
          netSalary: salary.netSalary,
          currency: salary.currency,
          salaryType: salary.salaryType,
          effectiveDate: apiDate,
          isActive: salary.isActive,
          reason: salary.reason ?? null,
          notes: salary.notes ?? null
        };
        // Inicializa o novo salário com base no atual (pode ser ajustado pelo usuário)
        this.salaryRaisePayload = {
          baseSalary: salary.baseSalary,
          grossSalary: salary.grossSalary,
          netSalary: salary.netSalary,
          currency: salary.currency,
          salaryType: salary.salaryType,
          effectiveDate: apiDate,
          isActive: salary.isActive,
          reason: '',
          notes: ''
        };
      },
      error: () => {
        this.isLoadingCurrentSalary = false;
      }
    });
  }

  closeSalaryRaiseModal(): void {
    this.showSalaryRaiseModal = false;
    this.selectedStaffForRaise = null;
  }

  saveSalaryRaise(): void {
    if (!this.selectedStaffForRaise || !this.selectedStaffForRaise.Id) {
      return;
    }

    const { baseSalary, grossSalary, netSalary, currency, salaryType, effectiveDate, isActive, reason, notes } = this.salaryRaisePayload;
    if (!baseSalary || !grossSalary || !netSalary || !currency || !salaryType || !effectiveDate || !reason || !reason.toString().trim()) {
      this.error = 'Preencha todos os campos obrigatórios do ajuste salarial, incluindo o motivo do aumento.';
      return;
    }

    this.isSavingSalaryRaise = true;
    this.error = null;

    this.staffService.updateStaffSalary(this.selectedStaffForRaise.Id, {
      baseSalary: Number(baseSalary),
      grossSalary: Number(grossSalary),
      netSalary: Number(netSalary),
      currency,
      salaryType,
      effectiveDate,
      isActive,
      reason: reason.toString().trim(),
      notes: notes ? notes.toString().trim() : ''
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSavingSalaryRaise = false;
        this.closeSalaryRaiseModal();
        this.loadStaff();
        this.errorService.showSuccessMessage('Salario atualizado com sucesso');
      },
      error: () => {
        this.isSavingSalaryRaise = false;
      }
    });
  }

  // ===== DISSÍDIO COLETIVO (CLT) =====

  openDissidioModal(): void {
    const today = new Date().toISOString().split('T')[0];
    this.dissidioPayload = {
      percentage: 0,
      effectiveDate: today
    };
    this.dissidioResult = null;
    this.showDissidioModal = true;
  }

  closeDissidioModal(): void {
    this.showDissidioModal = false;
  }

  applyDissidio(): void {
    const { percentage, effectiveDate } = this.dissidioPayload;
    if (!percentage || percentage <= 0 || !effectiveDate) {
      this.error = 'Informe um percentual maior que zero e a data de vigência.';
      return;
    }

    this.isApplyingDissidio = true;
    this.error = null;

    this.staffService.applyDissidio({
      percentage: Number(percentage),
      effectiveDate
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.isApplyingDissidio = false;
        this.dissidioResult = result;
        this.loadStaff();
        this.errorService.showSuccessMessage('Dissídio aplicado com sucesso para funcionários CLT.');
      },
      error: () => {
        this.isApplyingDissidio = false;
      }
    });
  }

  exportDissidioToExcel(): void {
    if (!this.dissidioResult || !this.dissidioResult.details || this.dissidioResult.details.length === 0) {
      this.errorService.showInfoMessage('Não há dados de dissídio para exportar.');
      return;
    }

    const r = this.dissidioResult;

    const escape = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      const str = String(value).replace(/"/g, '""');
      return `"${str}"`;
    };

    const header = [
      'StaffId',
      'Nome',
      'Matricula',
      'TipoContrato',
      'SalarioAnterior',
      'SalarioNovo',
      'Acrescimo',
      'Atualizado',
      'Motivo'
    ];

    const lines: string[] = [];
    // Linha de resumo no topo
    lines.push(`"Percentual";"${r.percentage}"`);
    lines.push(`"DataEfetiva";"${r.effectiveDate}"`);
    lines.push(`"TotalFuncionarios";"${r.totalEmployees}"`);
    lines.push(`"Atualizados";"${r.updatedEmployees}"`);
    lines.push(`"Ignorados";"${r.skippedEmployees}"`);
    lines.push(
      `"TotalAnterior";"${r.totalPreviousSalary}";"TotalNovo";"${r.totalNewSalary}";"AcrescimoTotal";"${r.totalIncrease}"`
    );
    lines.push(''); // linha em branco

    lines.push(header.join(';'));

    r.details.forEach(d => {
      const row = [
        escape(d.staffId),
        escape(d.staffName),
        escape(d.registration),
        escape(d.contractType),
        escape(d.previousSalary),
        escape(d.newSalary),
        escape(d.increase),
        escape(d.updated ? 'Atualizado' : 'Ignorado'),
        escape(d.reason || '')
      ];
      lines.push(row.join(';'));
    });

    const csvContent = lines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    link.download = `dissidio_clt_${today}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  // ===== RELATÓRIOS =====

  openReportModal(): void {
    this.showReportModal = true;
  }

  closeReportModal(): void {
    this.showReportModal = false;
    this.resetReportForm();
  }

  resetReportForm(): void {
    // Resetar campos para valores padrão
    this.availableFields.forEach(field => {
      field.checked = ['id', 'fullName', 'registration', 'department', 'position', 'hireDate'].includes(field.key);
    });
    this.reportFilters = {
      department: '',
      isActive: null,
      contractType: null
    };
    this.reportOptions = {
      includeContacts: false,
      includeAddresses: false,
      includeSalary: false,
      includeBenefits: false
    };
  }

  toggleField(fieldKey: string): void {
    const field = this.availableFields.find(f => f.key === fieldKey);
    if (field) {
      field.checked = !field.checked;
    }
  }

  selectAllFields(): void {
    this.availableFields.forEach(field => field.checked = true);
  }

  deselectAllFields(): void {
    this.availableFields.forEach(field => field.checked = false);
  }

  generateReport(): void {
    const selectedFields = this.availableFields
      .filter(field => field.checked)
      .map(field => field.key);

    if (selectedFields.length === 0) {
      this.error = 'Selecione pelo menos um campo para exportar';
      return;
    }

    this.isGeneratingReport = true;
    this.error = null;

    const exportData: any = {
      fields: selectedFields
    };

    // Adicionar filtros apenas se preenchidos
    const filters: any = {};
    if (this.reportFilters.department?.trim()) {
      filters.department = this.reportFilters.department.trim();
    }
    if (this.reportFilters.isActive !== null) {
      filters.isActive = this.reportFilters.isActive;
    }
    if (this.reportFilters.contractType && this.reportFilters.contractType.trim()) {
      filters.contractType = this.reportFilters.contractType.trim();
    }
    if (Object.keys(filters).length > 0) {
      exportData.filters = filters;
    }

    // Adicionar opções apenas se alguma estiver marcada
    const options: any = {};
    if (this.reportOptions.includeContacts) options.includeContacts = true;
    if (this.reportOptions.includeAddresses) options.includeAddresses = true;
    if (this.reportOptions.includeSalary) options.includeSalary = true;
    if (this.reportOptions.includeBenefits) options.includeBenefits = true;
    if (Object.keys(options).length > 0) {
      exportData.options = options;
    }

    this.staffService.exportStaffToExcel(exportData).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (blob: Blob) => {
        this.downloadExcelFile(blob);
        this.isGeneratingReport = false;
        this.closeReportModal();
      },
      error: (err) => {
        this.isGeneratingReport = false;
        this.error = 'Erro ao gerar relatório Excel';
      }
    });
  }

  private downloadExcelFile(blob: Blob): void {
    try {
      // Garantir que o blob tenha o tipo MIME correto para Excel
      let excelBlob = blob;
      if (!blob.type || !blob.type.includes('spreadsheet')) {
        excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }

      // Criar URL temporária para o blob
      const url = window.URL.createObjectURL(excelBlob);
      
      // Criar elemento <a> para download
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_funcionarios_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.style.display = 'none';
      
      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      
      // Limpar após um pequeno delay para garantir que o download iniciou
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      this.errorService.showSuccessMessage('Relatório Excel gerado e baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer download do arquivo Excel:', error);
      this.error = 'Erro ao fazer download do arquivo Excel';
    }
  }
}
